import { ChangeDetectionStrategy, Component, DOCUMENT, inject, computed, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Config } from "@cbcore/services/config/config.service";
import { GT_ENABLED_TOKEN } from '../../../group-tickets/group-tickets.tokens';
import { selectInfo } from '../../../group-tickets/gt-bridge';
import { WindowService } from "@cbcore/services/utils/window/window.service";
import {
  AppState,
  selectConfiguration,
  selectMicrositeSessions,
  setMenuState,
  clearMenuState
} from '@cbcore/NGRX/core.state';
import { Store } from '@ngrx/store';
import { loadList } from '@cbcore/NGRX/syndicates/syndicates.actions';
import { loadTicketList } from "@cbcore/NGRX/tickets/tickets.actions";
import { selectLeftMenu, selectPoolExtraModel } from '@cbcore/NGRX/pool/pool.selectors';
import { setDisplayState } from "@cbcore/NGRX/configuration/configuration.actions";
import { Menu } from "@cbcore/NGRX/ui-states/ui.model";
import { selectMenuState } from "@cbcore/NGRX/ui-states/ui.selectors";
import { CB_UI_ENV } from '../../../environment/cb-ui-env.token';
import { TrackingDirective } from '../../../helpers/directives/tracking.directive';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { TranslocoModule } from '@jsverse/transloco';
import { PipesModule } from '@cbcore/pipes/pipes.module';
import { RouterModule } from '@angular/router';

@Component({
  selector: 'component-menu',
  templateUrl: './menu.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
  imports: [RouterModule,
    PipesModule,
    TranslocoModule,
    CommonModule,
    TrackingDirective]
})
export class MenuComponent {
  private _env = inject(CB_UI_ENV);
  protected _router = inject(Router);
  protected _config = inject(Config);
  protected windowRef = inject(WindowService);
  protected store = inject(Store<AppState>);
  protected _document = inject(DOCUMENT);

  private currentUrl = toSignal(
    this._router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(e => e.urlAfterRedirects)
    ),
    { initialValue: this._router.url }
  );

  // Use selectSignal for store selectors
  private menuStateSignal = this.store.selectSignal(selectMenuState);
  private configurationSignal = this.store.selectSignal(selectConfiguration);
  private leftMenuSignal = this.store.selectSignal(selectLeftMenu);
  private micrositeSessionsSignal = this.store.selectSignal(selectMicrositeSessions);
  private poolExtraModelSignal = this.store.selectSignal(selectPoolExtraModel);
  // Only subscribe to GT state when the feature module is present — avoids the
  // createFeatureSelector dev warning and unnecessary store subscriptions.
  private gtEnabled = inject(GT_ENABLED_TOKEN);
  private gtInfoSignal = this.gtEnabled
    ? this.store.selectSignal(selectInfo)
    : signal(undefined);

  // Use toSignal for router events
  private navigationEnd = toSignal(
    this._router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    )
  );

  // Computed signals for derived values
  menuState = computed(() => {
    const state = this.menuStateSignal();
    return state || {
      activatedChild: null,
      isResults: false,
      isSyndicates: false,
      resultsSection: null
    };
  });

  displayState = computed(() => this.configurationSignal()?.displayState);

  singleSportMicrosite = computed(() =>
    this.configurationSignal()?.displayState?.microsite_type?.length === 1
  );

  leftMenu = computed(() => this.leftMenuSignal());

  isLoggedIn = computed(() => !!this.micrositeSessionsSignal()?.ext_id);

  isAgeVerified = computed(() =>
    this.micrositeSessionsSignal()?.age_verified || this.skipVerifyAge
  );

  freeplays = computed(() => this.micrositeSessionsSignal()?.freeplays || 0);

  noContentLink = computed(() => (this.poolExtraModelSignal()?.poolCounts?.['ALL'] ?? 1) === 0);

  groupTicketInvites = computed(() =>
    this.gtEnabled ? (this.gtInfoSignal()?.invitation_count || 0) : 0
  );

  haveGroupTickets = computed(() =>
    this.gtEnabled && (this.groupTicketInvites() > 0)
  );

  // Navigation state computed signals
  isOnGTicketsPage = computed(() => {
    const event = this.navigationEnd();
    if (!event) return true;
    return event.url.indexOf('tickets') > 0 &&
      event.url.indexOf('group') > 0 &&
      event.url.indexOf('pools') > 0;
  });

  isOnSyndicatesPages = computed(() => {
    const event = this.navigationEnd();
    if (!event) return true;
    return event.url.indexOf('syndicates/create') > 0;
  });

  // Menu visibility computed signals
  hasSportsMenuItem = computed(() => {
    if (this.noContentLink()) return false;
    if (!this.displayState()?.microsite_type?.includes('SPORTS')) return false;
    return (this.poolExtraModelSignal()?.poolCounts?.['SPORTS'] ?? 0) > 0;
  });

  hasRacingMenuItem = computed(() => {
    if (this.noContentLink()) return false;
    if (!this.displayState()?.microsite_type?.includes('RACING')) return false;
    return (this.poolExtraModelSignal()?.poolCounts?.['RACING'] ?? 0) > 0;
  });

  hasFantasyMenuItem = computed(() => {
    if (this.noContentLink()) return false;
    if (!this.displayState()?.microsite_type?.includes('FANTASY')) return false;
    return (this.poolExtraModelSignal()?.poolCounts?.['FANTASY'] ?? 0) > 0;
  });

  hasGenericMenuItem = computed(() => {
    if (this.noContentLink()) return false;
    if (!this.displayState()?.microsite_type?.includes('GENERIC')) return false;
    return (this.poolExtraModelSignal()?.poolCounts?.['GENERIC'] ?? 0) > 0;
  });

  // Config-based properties (initialized once)
  public navIsFixed: boolean = false;
  public stickyHeader: boolean;
  public isCashOut: boolean;
  public rootUrl: string;
  public helpBaseLink: string;
  public syndicatesEnabled: boolean;
  public hideSyndicatesTab: boolean;
  public hideFreeplayTab: boolean;
  public enableFreeplay: boolean;
  public enableUnauthCashout: boolean;
  public freePlayStandalone: boolean;
  public haveHomeLink: boolean;
  public additionalLeftNavMenu: boolean;
  public skipVerifyAge: boolean;
  public imagesUrl: string;
  public isCB: boolean = this._env.isCB ?? false;
  public _window: Window;

  constructor() {
    this._window = this.windowRef.nativeWindow;

    // Initialize config-based properties
    this.helpBaseLink = this._config.CONFIG.helpBaseLink;
    this.rootUrl = this._config.CONFIG.rootUrl || '';
    this.stickyHeader = this._config.CONFIG.stickyHeader;
    this.syndicatesEnabled = this._config.CONFIG.enableSyndicates;
    this.hideSyndicatesTab = this._config.CONFIG.hideSyndicatesTab;
    this.hideFreeplayTab = this._config.CONFIG.hideFreeplayTab;
    this.enableFreeplay = this._config.CONFIG.enableFreeplay;
    this.enableUnauthCashout = this._config.CONFIG.enableUnauthCashout;
    this.freePlayStandalone = this._config.CONFIG.freePlayStandalone;
    this.haveHomeLink = this._config.CONFIG.haveHomepage;
    this.additionalLeftNavMenu = this._config.CONFIG.additionalLeftNavMenu;
    this.skipVerifyAge = this._config.CONFIG.skipVerifyAge;
    this.imagesUrl = this._config.CONFIG.imagesUrl;
    this.isCashOut = /unauth/.test(this._router.url);
  }

  activated(route: string): boolean {
    const url = this.currentUrl() ?? '';
    if (!url) return false;

    const isSpecialRoute =
      url.includes('tickets') ||
      url.includes('syndicates') ||
      url === '/' ||
      url.includes('freeplay');

    switch (route) {
      case this.rootUrl:
      case '':
        return url === '/' || url === this.rootUrl;
      case '/no-content':
        return url === '/no-content';
      case '/syndicates':
        return url.includes('syndicates') && !url.includes('tickets');
      case '/tickets':
        return url.includes('tickets');
      case '/freeplay':
        return url === '/freeplay';
      default:
        return !isSpecialRoute && url.startsWith(route);
    }
  }

  setMenuState(activeSport: string, isResult?: boolean): void {
    const menuState: Menu = {
      activatedChild: activeSport,
      isSyndicates: false,
      isResults: isResult || false,
      resultsSection: isResult ? activeSport : null
    };
    this.store.dispatch(setMenuState({ menuState: menuState }));
  }

  __clearMenuState(): void {
    this.store.dispatch(clearMenuState());
  }

  resetSyndicateList(): void {
    this.store.dispatch(loadList({}));
    this.__clearMenuState();
  }

  resetTicketFilter(): void {
    this.store.dispatch(setDisplayState({ displayState: { view: 'ALL' } }));
    this.store.dispatch(loadTicketList({}));
    this.__clearMenuState();
  }

  setDisplayState(type: string): void {
    this.store.dispatch(setDisplayState({ displayState: { view: type } }));
  }
}
